// PrivacyGateHost — the small impure host that drives the pure `PrivacyGate`.
//
// Mounted by `AppShellHost` after sign-in, before the shell, whenever the
// server refuses a read with 403 `privacy_terms_not_accepted`
// (External.PrivacyTermsNotAccepted; the server is the single source of
// truth, per person). It owns only
// the view-ephemeral form draft (ticks + the optional toggle + submit status;
// none of it survives a reload until submitted) and the event-boundary port
// calls:
//
//   accept  → `acceptPrivacyTerms`, then `setSummaryConsent(true)` only if the
//             Author opted in (off by default, never pre-ticked), then
//             `onAccepted` (the root reloads: every boot read the refusal
//             blocked runs again, so the gate closes because the server now
//             serves data, not because of a local flag).
//   decline → sign out, then reload (back to the public front door).

export { PrivacyGateHost };

import { Result } from "@bilda/utils";
import { useState } from "react";
import {
    PrivacyGate,
    type PrivacyGateState,
} from "../../components/PrivacyGate/PrivacyGate.js";
import type { AppPorts } from "../ports.js";

type PrivacyGateHostProps = {
    readonly ports: Pick<
        AppPorts,
        "acceptPrivacyTerms" | "setSummaryConsent" | "signOut"
    >;
    readonly onAccepted: () => void;
};

const PrivacyGateHost = ({ ports, onAccepted }: PrivacyGateHostProps) => {
    const [state, setState] = useState<PrivacyGateState>({
        termsRead: false,
        rightsConfirmed: false,
        summaryOptIn: false,
        status: { _tag: "editing" },
    });

    const onAccept = async (): Promise<void> => {
        setState((s) => ({ ...s, status: { _tag: "submitting" } }));
        const rAccept = await ports.acceptPrivacyTerms();
        if (Result.isErr(rAccept)) {
            setState((s) => ({ ...s, status: { _tag: "failed" } }));
            return;
        }
        // The optional choice is a separate intent with its own command. A
        // failure here does not block entry: the toggle stays visible (and
        // honest, server-projected) under Settings → Assistant.
        if (state.summaryOptIn) await ports.setSummaryConsent(true);
        onAccepted();
    };

    const onDecline = async (): Promise<void> => {
        await ports.signOut();
        window.location.reload();
    };

    return (
        <PrivacyGate
            state={state}
            onTermsReadChange={(termsRead) =>
                setState((s) => ({ ...s, termsRead }))
            }
            onRightsConfirmedChange={(rightsConfirmed) =>
                setState((s) => ({ ...s, rightsConfirmed }))
            }
            onSummaryOptInChange={(summaryOptIn) =>
                setState((s) => ({ ...s, summaryOptIn }))
            }
            onAccept={onAccept}
            onDecline={onDecline}
        />
    );
};
